/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';
import React, { useState, createContext, useContext } from 'react';

interface ChannelOverlayContextProps {
  buttonHooks: {
    backClicked: number | null;
    nextClicked: number | null;
    setEnableNext: (enable: boolean) => void;
    setFinished: (complete: boolean) => void;
  };
  enableNext: boolean | null;
  showNextBack: boolean | null;
  finished: boolean | null;
  setShowNextBack: (value: boolean) => void;
}

export const ChannelOverlayContext = createContext({
  buttonHooks: {
    backClicked: null,
    nextClicked: null,
    setEnableNext: _enable => {},
    setFinished: _complete => {}
  },
  enableNext: null,
  showNextBack: null,
  finished: null,
  setShowNextBack: _val => {}
});

export const useChannelOverlayContext = () => useContext(ChannelOverlayContext);

export const ChannelOverlayProvider: React.FC<
  ChannelOverlayContextProps
> = props => {
  return (
    <ChannelOverlayContext.Provider value={_.omit(props, ['children'])}>
      {props.children}
    </ChannelOverlayContext.Provider>
  );
};

export const useChannelOverlay = () => {
  const [backClicked, setBackClicked] = useState<number | null>(null);
  const [nextClicked, setNextClicked] = useState<number | null>(null);
  const [showNextBack, setShowNextBack] = useState<boolean>(false);
  const [enableNext, setEnableNext] = useState<boolean>(false);
  const [finished, setFinished] = useState<boolean>(false);

  const handleNext = () => {
    // pass through to whatever callback is passed.
    setBackClicked(null);
    setNextClicked(Date.now());
  };

  return {
    buttonHooks: {
      backClicked,
      nextClicked,
      setEnableNext,
      setFinished
    },
    enableNext,
    showNextBack,
    finished,
    setShowNextBack,
    handleNext
  };
};
